/**
 * Listing Type Constants and Helpers
 *
 * Centralized listing type definitions for Sharetribe listings
 */

// Listing Type IDs (must match Sharetribe Console configuration)
export const LISTING_TYPES = {
  TICKET_REQUEST: 'testing-bids', // Reverse Price Negotiation - for bids (matches Sharetribe Console)
  TICKET_SALE: 'buying-tickets', // Regular ticket sales (for selling tickets)
  // Add other listing types as needed
};

export const PRICE_VERIFICATION_STATUSES = {
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified',
  UNDER_REVIEW: 'under_review',
  MANUAL_REVIEW: 'pending_manual_review',
};

/**
 * Check if a listing is a ticket request (Reverse listing)
 * @param {Object} listing - Sharetribe listing object
 * @returns {boolean}
 */
export const isTicketRequest = listing => {
  if (!listing) return false;
  // listingType is stored in publicData, not directly in attributes
  const listingType =
    listing?.attributes?.publicData?.listingType ||
    listing?.attributes?.listingType ||
    listing?.type;
  return listingType === LISTING_TYPES.TICKET_REQUEST;
};

/**
 * Check if a listing is a regular ticket sale
 * @param {Object} listing - Sharetribe listing object
 * @returns {boolean}
 */
export const isTicketSale = listing => {
  if (!listing) return false;
  const listingType = listing?.attributes?.listingType || listing?.type;
  return listingType === LISTING_TYPES.TICKET_SALE;
};

/**
 * Get custom field value from listing publicData
 * @param {Object} listing - Sharetribe listing object
 * @param {string} fieldName - Name of the custom field
 * @returns {any}
 */
export const getListingCustomField = (listing, fieldName) => {
  if (!listing?.attributes?.publicData) return null;
  return listing.attributes.publicData[fieldName] || null;
};

/**
 * Get event details from ticket request listing
 * @param {Object} listing - Sharetribe listing object
 * @returns {Object} Event details
 */
export const getTicketRequestDetails = listing => {
  if (!isTicketRequest(listing)) return null;

  const publicData = listing?.attributes?.publicData || {};
  // Check multiple possible field names for buyer's message (Console may use different naming)
  // Common Console field names: 'message', camelCase (buyersMessage), snake_case (buyers_message), or exact label ('Buyers message')
  const buyersMessage =
    publicData.message ||
    publicData.buyersMessage ||
    publicData.buyers_message ||
    publicData['Buyers message'] ||
    null;

  // Get seat details (could be stored as seat_details or pub_seat_details)
  const seatDetails =
    publicData.seat_details || publicData.pub_seat_details || publicData.seatDetails || null;

  return {
    eventDate: publicData.event_date || null,
    eventDateTime: publicData.event_date_time || publicData.eventDateTime || null,
    eventTime: publicData.event_time || publicData.eventTime || publicData.localTime || null,
    venue: publicData.venue || null,
    eventLocation: publicData.event_location || null,
    ticketType: publicData.ticket_type || 'general',
    seatDetails: seatDetails,
    numberOfTickets: publicData.number_of_tickets || 1,
    minPrice: publicData.min_price || null,
    maxPrice: publicData.max_price || null,
    crewId: publicData.crew_id || null,
    attractionName: publicData.attraction_name || null,
    buyersMessage: buyersMessage,
  };
};

/**
 * Format price range for display
 * @param {Object} listing - Sharetribe listing object
 * @returns {string} Formatted price range
 */
export const getPriceRange = listing => {
  if (!listing) return null;

  // Directly access publicData to ensure we get the values
  const publicData = listing?.attributes?.publicData || {};
  const minPrice = publicData.min_price;
  const maxPrice = publicData.max_price;

  // Convert values (stored in subunits) to display dollars and format as whole amounts.
  const toWholeDollarDisplay = value => {
    if (value == null || value === '') return null;
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(num)) return null;
    const dollars = num >= 100 ? num / 100 : num;
    return Math.round(dollars).toLocaleString();
  };

  const minPriceNum = toWholeDollarDisplay(minPrice);
  const maxPriceNum = toWholeDollarDisplay(maxPrice);

  // Only proceed if at least one price is valid
  if (!minPriceNum && !maxPriceNum) {
    return 'Price negotiable';
  }

  // Format the price range
  if (minPriceNum && maxPriceNum) {
    return `$${minPriceNum} - $${maxPriceNum}`;
  } else if (minPriceNum) {
    return `From $${minPriceNum}`;
  } else if (maxPriceNum) {
    return `Up to $${maxPriceNum}`;
  }

  return 'Price negotiable';
};
